/**
 * DXF handle allocation (docs/opencadstudio-parity/01-native-dxf-export.md
 * §3.2 decision 2). Entity handles are deterministic per session: an
 * entity id maps to one handle for the whole session (repeated exports of
 * the same drawing stay consistent; the map is NOT persisted into .yap —
 * imports regenerate handles). Table records come from a disjoint pool so
 * entity and table numbers can never collide.
 */

/** Entity pool starts at handle 0x100 (below that is reserved space). */
const ENTITY_POOL_BASE = 0x100;
/** Table pool base — leaves ~16M entity slots before any overlap. */
const TABLE_POOL_BASE = 0x1000000;

/** Session-lifelong entity id → handle map (module scope). */
const sessionEntityHandles = new Map<string, string>();
/** Monotonic entity pool counter (module scope, never rewound). */
let entityCounter = ENTITY_POOL_BASE;

const toHex = (n: number): string => n.toString(16).toUpperCase();

export class HandleAllocator {
    private tableCounter = TABLE_POOL_BASE;

    /** Handle for a drawing entity (stable for the whole session). */
    entityHandle(id: string): string {
        const existing = sessionEntityHandles.get(id);
        if (existing) return existing;
        const handle = toHex(entityCounter++);
        sessionEntityHandles.set(id, handle);
        return handle;
    }

    /** Pre-register a known id → handle mapping (used by table pools). */
    register(id: string, handle: string): void {
        if (!sessionEntityHandles.has(id)) sessionEntityHandles.set(id, handle);
    }

    /** Next handle from the table record pool (layers, ltypes, styles…). */
    nextTableHandle(): string {
        return toHex(this.tableCounter++);
    }

    /**
     * $HANDSEED value: the next free handle across both pools, always
     * strictly greater than every handle emitted by this writer.
     */
    handSeed(): string {
        return toHex(Math.max(entityCounter, this.tableCounter) + 1);
    }
}
